import { Alert, Grid, GridSize, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardIncidents from "../../components/incident-model/dash-incidents";
import AdditionalDetailsModal from "../../components/integrated-dispatch-feed/additional-details-modal";
import {
  IncidentStoreError,
  IncidentStoreSuccess,
} from "../../enums/store-messages/incident";
import { DispatchEvent, IRootReducer } from "../../reducers";
import { UTCFromTimeFrameUtil, UTCToOneMinuteAheadUtil } from "../../utils/date-utils";
import {
  getDispatchFeedEventRequest,
  getDispatchFeedEventsRequest,
  getFeedStatusRequest,
  getNoteworthyEventsRequest,
  getPinnedDispatchEventsRequest,
  getTrafficCamLayerRequest,
  getOpenIncidentsRequest,
  updateUserPreferencesRequest,
} from "../../actions";
import { DispatchEventFeedGrid } from "../../components";
import { MapLayers, MarkerTypes } from "../../enums/map";
import { DispatchFeedStoreError } from "../../enums/store-messages/dispatch";
import { FeedView } from "../../enums/feed-view";
import { awsRum, customTheme } from "../..";
import { RUMPage } from "../../enums/rum";
import { MarkerData } from "../../reducers/states/map";
import VccMap from "../../components/map/vcc-map";

export default function DashboardPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [idfDetails, setIdfDetails] = useState({} as DispatchEvent);
  const [idfDetailsOpen, setIdfDetailsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const theme = useTheme();
  const isLessThanLg = useMediaQuery(theme.breakpoints.down('lg'));
  const incidentStore = useSelector((state: IRootReducer) => state.vccIncidentReducer);
  const eventStore = useSelector((state: IRootReducer) => state.dispatchEventFeedReducer);
  const userStore = useSelector((state: IRootReducer) => state.userInfoReducer);
  const mapStore = useSelector((state: IRootReducer) => state.mapReducer);

  // User preferences
  // - JOG filters
  const jogFilters = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.jogFilters);
  // - Map layers
  const selectedMapLayers = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.mapLayers);
  const selectedMarkerLayers = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.markerLayers);
  // - Event feed filters
  const timeFrame = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.timeFrame);
  const feedView = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.feedView);
  const showOpenDispatchesOnly = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.showOpenDispatchesOnly);
  // - Layout
  const isMapCenter = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.isMapCenter);
  const isLeftPanelCollapsed = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.isLeftPanelCollapsed);
  const isRightPanelCollapsed = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.isRightPanelCollapsed);

  const getFeedAndFeedStatus = useCallback(
    (fromTimeStamp: Date, toTimeStamp: Date) => {
      dispatch(getPinnedDispatchEventsRequest(fromTimeStamp, toTimeStamp));
      dispatch(getFeedStatusRequest());

      switch (feedView) {
        case FeedView.ALL:
          dispatch(
            getDispatchFeedEventsRequest(
              jogFilters.join(","),
              fromTimeStamp,
              toTimeStamp,
              showOpenDispatchesOnly,
              false, // set map data
            )
          );
          break;
        case FeedView.NOTEWORTHY:
          dispatch(getNoteworthyEventsRequest());
          break;
      }
    },
    [dispatch, jogFilters, feedView, showOpenDispatchesOnly]
  );

  useEffect(() => {
    try {
      awsRum.recordPageView(RUMPage.DASHBOARD);
    }
    catch {
      // Swallow rum errors
    }
  }, [])


  // Update View point to default
  useEffect(() => {
    dispatch(getTrafficCamLayerRequest()); // Review: Move this into the map init    
  }, [dispatch]);

  // Update timeFrame dep entities: incidents, dispatch events, refresh timer
  useEffect(() => {
    // Set up timer for grid
    const refreshGrid = () => {
      getFeedAndFeedStatus(
        UTCFromTimeFrameUtil(timeFrame),
        UTCToOneMinuteAheadUtil()
      );
    };
    let gridTimer = setInterval(refreshGrid, 45000);

    // If the timer is valid, clear the current timer, fetch the latest, then reset the timer
    // Wait for user preferences to load for checking for jogFilters to be truthy
    if (timeFrame !== 0 && jogFilters) {
      clearInterval(gridTimer);
      getFeedAndFeedStatus(
        UTCFromTimeFrameUtil(timeFrame),
        UTCToOneMinuteAheadUtil()
      );
      gridTimer = setInterval(refreshGrid, 45000);

      // Also fetch the latest Active Incident details
      dispatch(getOpenIncidentsRequest(jogFilters.join(",")));
    }

    // useEffect cleanup - turn off the timer when this component is closed
    return () => clearInterval(gridTimer);
  }, [dispatch, getFeedAndFeedStatus, timeFrame, feedView, jogFilters]);

  // Retrieve latest incidents when new notifications come through
  useEffect(() => {
    if (incidentStore.incidentNotifs.length > 0 && jogFilters) {
      dispatch(getOpenIncidentsRequest(jogFilters.join(",")));
    }
  }, [dispatch, incidentStore.incidentNotifs, jogFilters]);

  // Refresh incidents on a successful save
  useEffect(() => {
    if (incidentStore.successMessage === IncidentStoreSuccess.INCIDENT_SAVE
      && jogFilters
    ) {
      dispatch(getOpenIncidentsRequest(jogFilters.join(",")));
    }
  }, [dispatch, incidentStore.successMessage, jogFilters]);

  // Handle errors from retrieving data
  useEffect(() => {
    if (incidentStore.failureMessage === IncidentStoreError.VCC_INCIDENTS_GET) {
      setErrorMessage(incidentStore.failureMessage);
    }
  }, [incidentStore.failureMessage]);

  useEffect(() => {
    switch (eventStore.failureMessage) {
      case DispatchFeedStoreError.DISPATCH_EVENT_FEED_GET:
      case DispatchFeedStoreError.DISPATCH_EVENTS_PIN_GET:
        setErrorMessage(eventStore.failureMessage);
        break;
    }
  }, [eventStore.failureMessage]);

  useEffect(() => {
    setIdfDetails(eventStore.dispatchEvent);
  }, [eventStore.dispatchEvent]);

  const handleMarkerOnClick = (markerType: string, markerId: string): void => {
    awsRum.recordEvent("dashboard-map-marker-click", {
      markerType: markerType,
      markerId: markerId
    });

    switch (markerType) {
      case MarkerTypes.DISPATCH:
        dispatch(getDispatchFeedEventRequest(markerId));
        setIdfDetailsOpen(true);
        break;
      case MarkerTypes.INCIDENT:
        navigate(`/incident-model/${markerId}`);
        break;
    }
  };

  const saveMapLayerPreferences = (mapLayers: string[]) => {
    dispatch(updateUserPreferencesRequest({
      ...userStore.userPreferences,
      mapLayers: mapLayers,
    }));
  }

  const saveMarkerLayerPreferences = (markerLayers: string[]) => {
    dispatch(updateUserPreferencesRequest({
      ...userStore.userPreferences,
      markerLayers: markerLayers,
    }));
  }

  // Format map marker data
  const markerDataSets: MarkerData[] = [
    {
      category: MapLayers.DISPATCH_EVENTS,
      geoData: mapStore.dispatchGeo,
      color: customTheme.marker.DispatchEvent!,
    },
    {
      category: MapLayers.VCC_INCIDENTS,
      geoData: mapStore.incidentGeo,
      color: customTheme.marker.ActiveIncident!,
    },
  ];

  const isLeftCollapsed = () => {
    return !isLessThanLg && isLeftPanelCollapsed;
  }

  const isRightCollapsed = () => {
    return !isLessThanLg && isRightPanelCollapsed;
  }

  const setIsMapCenter = () => {
    dispatch(updateUserPreferencesRequest({
      ...userStore.userPreferences,
      isMapCenter: !isMapCenter,
    }));
  };

  const setIsLeftPanelCollapsed = () => {
    dispatch(updateUserPreferencesRequest({
      ...userStore.userPreferences,
      isLeftPanelCollapsed: !isLeftPanelCollapsed,
    }));
  };

  const setIsRightPanelCollapsed = () => {
    dispatch(updateUserPreferencesRequest({
      ...userStore.userPreferences,
      isRightPanelCollapsed: !isRightPanelCollapsed,
    }));
  };
  let lgLeftCols: GridSize = isLeftCollapsed() ? "auto" : 3;
  let lgRightCols: GridSize = isRightCollapsed() ? "auto" : 3;
  const lgMapCols: GridSize = isLessThanLg ? 6 : lgLeftCols;

  const getEventGrid = () => {
    return (
      <Grid
        item
        pr={isMapCenter ? 0 : 1}
        xs={12}
        lg={isMapCenter ? lgLeftCols : true}
        style={{
          // maxWidth is for better display on very wide monitors
          ...(isMapCenter && !isLessThanLg && { maxWidth: "684px" }),
          // minWidth helps prevent a short grid height because of too much wrapping of filters and paging controls
          ...(!isLeftCollapsed() && { minWidth: "420px" })
        }}
      >
        <DispatchEventFeedGrid
          isDashDisplay={true}
          isScreenLessThanLarge={isLessThanLg}
          isSideDisplay={isMapCenter}
          isCollapsed={isLeftCollapsed()}
          handleDashCenter={() => setIsMapCenter()}
          handleDashCollapse={() => setIsLeftPanelCollapsed()}
        />
      </Grid>
    )
  }

  return (
    <>
      {selectedMapLayers && selectedMarkerLayers && (
        <Grid container>
          {isMapCenter ? getEventGrid() : undefined}
          <Grid item p={1} xs={12} lg={isMapCenter ? true : lgMapCols}>
            <VccMap
              props={{
                markerData: markerDataSets,
                initLayers: selectedMapLayers,
                initMarkerSets: selectedMarkerLayers,
                allAvailableLayers: [MapLayers.CAMERA_LAYER, MapLayers.TRAFFIC_LAYER, MapLayers.CONSTRUCTION_EVENTS_DATA, MapLayers.MAJOR_EVENTS],
                allAvailableMarkerSets: [MapLayers.DISPATCH_EVENTS, MapLayers.VCC_INCIDENTS],
                markerOnClick: handleMarkerOnClick,
                handleSaveMapLayers: saveMapLayerPreferences,
                handleSaveMarkerLayers: saveMarkerLayerPreferences,
              }}
            />
          </Grid>
          {!isMapCenter ? getEventGrid() : undefined}
          <Grid item xs={12} lg={lgRightCols}
            // maxWidth is for better display on very wide monitors
            style={{ ...(!isLessThanLg && { maxWidth: "420px" }) }}
          >
            <DashboardIncidents
              props={{
                dispatchAddtlInfo: eventStore.dispatchEvent,
                isCollapsed: isRightCollapsed(),
                isScreenLessThanLarge: isLessThanLg,
                handleCollapse: () => { setIsRightPanelCollapsed(); },
              }}
            />
          </Grid>
        </Grid>
      )}
      <AdditionalDetailsModal
        props={{
          isOpen: idfDetailsOpen,
          idfEvent: idfDetails,
          handleClose: () => setIdfDetailsOpen(false),
        }}
      />
      <Snackbar
        open={errorMessage !== ""}
        autoHideDuration={10000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
